import { useEffect, useRef, useState, type ReactNode } from "react";
import { useDropzone } from "react-dropzone";
import { useQueryClient } from "@tanstack/react-query";
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import {
    Close as RemoveIcon,
    CloudUpload as UploadIcon,
    ErrorOutline as FailedIcon,
    HourglassEmpty as UnconfirmedIcon,
    PlayArrow as RunningIcon,
    RadioButtonUnchecked as QueuedIcon,
} from "@mui/icons-material";
import { useAppDispatch } from "../../store/hooks";
import { setSnackAction } from "../../store/slices/snackSlice";
import lensService from "../../services/lensService";
import usePlaybooks from "./hooks/usePlaybooks";
import { apiErrorMessage, isGatewayCutOff, plural, readFileBase64 } from "./lensUtils";

const useStyles = makeStyles()((theme) => ({
    root: { padding: theme.spacing(4), marginBottom: theme.spacing(4) },
    zone: {
        border: "1px dashed #bbb",
        borderRadius: 8,
        padding: theme.spacing(4),
        textAlign: "center",
        cursor: "pointer",
        color: "#555",
        "&:hover": { borderColor: "#7a5cf0" },
    },
    zoneActive: { borderColor: "#7a5cf0", backgroundColor: "#f6f3ff" },
    row: { display: "flex", alignItems: "center", gap: theme.spacing(2), padding: theme.spacing(1, 2), marginTop: theme.spacing(1) },
    name: { flexGrow: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    note: { color: "#777" },
    actions: { display: "flex", gap: theme.spacing(2), marginTop: theme.spacing(3) },
}));

const APPEAR_EVERY_MS = 2000;
const APPEAR_FOR_MS = 5 * 60 * 1000;
const FAIL_MESSAGE = "Could not review this document.";

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type QueueState = "queued" | "starting" | "failed" | "unconfirmed";

interface QueuedFile {
    file: File;
    state: QueueState;
    note: string;
}

// "left": the page was closed part way, so nothing more should be started from it.
type StartOutcome = { status: "started" } | { status: "refused"; message: string } | { status: "unconfirmed" } | { status: "left" };

const stateIcon: Record<QueueState, ReactNode> = {
    queued: <QueuedIcon fontSize="small" style={{ color: "#bbb" }} />,
    starting: <RunningIcon fontSize="small" color="primary" />,
    failed: <FailedIcon fontSize="small" color="error" />,
    unconfirmed: <UnconfirmedIcon fontSize="small" style={{ color: "#777" }} />,
};

const RunUpload = () => {
    const { classes } = useStyles();
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const playbooks = usePlaybooks();
    const [playbookKey, setPlaybookKey] = useState("");
    const [clientName, setClientName] = useState("");
    const [isTest, setIsTest] = useState(false);
    const [queue, setQueue] = useState<QueuedFile[]>([]);
    const [busy, setBusy] = useState(false);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    // Reviewable: enabled, not archived, with at least one live approved provision.
    const options = (playbooks.data ?? []).filter((p) => p.enabled && !p.archived && p.provisionCount > 0);
    const selectedKey = options.some((p) => p.key === playbookKey) ? playbookKey : (options[0]?.key ?? "");
    const pendingCount = queue.filter((q) => q.state === "queued" || q.state === "failed").length;

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        multiple: true,
        disabled: busy,
        accept: { "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"] },
        onDrop: (accepted: File[]) =>
            setQueue((previous) => [
                ...previous,
                // Same name and size is the same document, which matters when a folder is dropped twice.
                ...accepted
                    .filter((file) => !previous.some((q) => q.file.name === file.name && q.file.size === file.size))
                    .map((file) => ({ file, state: "queued" as QueueState, note: "" })),
            ]),
        onDropRejected: (rejected) =>
            dispatch(setSnackAction(`Skipped ${plural(rejected.length, "file")} — only Word .docx files can be reviewed.`, "error")),
    });

    const setEntry = (file: File, changes: Partial<QueuedFile>) =>
        setQueue((previous) => previous.map((entry) => (entry.file === file ? { ...entry, ...changes } : entry)));

    // The POST runs to the end on the server, but the review writes its run row before any AI call, so once the row
    // exists the run log shows it. Only a refusal made before that is reported here.
    const startReview = async (file: File, documentBase64: string): Promise<StartOutcome> => {
        const identifier = crypto.randomUUID();
        const post: { settled: boolean; failure: unknown } = { settled: false, failure: null };

        const posted = lensService
            .createRun({
                identifier,
                playbookKey: selectedKey,
                documentName: file.name,
                clientName: clientName.trim() || undefined,
                isTest,
                documentBase64,
            })
            .catch((error) => {
                post.failure = error ?? new Error(FAIL_MESSAGE);
            })
            .finally(() => {
                post.settled = true;
                queryClient.invalidateQueries({ queryKey: ["lens", "runs"] });
            });

        const giveUpAt = Date.now() + APPEAR_FOR_MS;
        while (Date.now() < giveUpAt) {
            await Promise.race([wait(APPEAR_EVERY_MS), posted]);
            // A 404 means the row is not written yet.
            const run = await lensService.fetchRun(identifier, true).catch(() => null);
            if (!mounted.current) return { status: "left" };
            if (run || (post.settled && !post.failure)) return { status: "started" };
            if (post.settled && !isGatewayCutOff(post.failure))
                return { status: "refused", message: apiErrorMessage(post.failure, FAIL_MESSAGE) };
        }
        return { status: "unconfirmed" };
    };

    const process = async () => {
        const pending = queue.filter((entry) => entry.state === "queued" || entry.state === "failed");
        if (pending.length === 0 || !selectedKey) return;

        setBusy(true);
        let started = 0;
        let refused = 0;
        let unconfirmed = 0;
        for (const { file } of pending) {
            setEntry(file, { state: "starting", note: "Starting" });

            let outcome: StartOutcome;
            try {
                outcome = await startReview(file, await readFileBase64(file));
            } catch {
                outcome = { status: "refused", message: "Could not read this file." };
            }
            if (outcome.status === "left") return;

            if (outcome.status === "started") {
                setQueue((previous) => previous.filter((entry) => entry.file !== file));
                queryClient.invalidateQueries({ queryKey: ["lens", "runs"] });
                started += 1;
            } else if (outcome.status === "unconfirmed") {
                setEntry(file, { state: "unconfirmed", note: "Not confirmed yet — refresh the run log to check." });
                unconfirmed += 1;
            } else {
                setEntry(file, { state: "failed", note: outcome.message });
                dispatch(setSnackAction(outcome.message, "error"));
                refused += 1;
            }
        }

        setBusy(false);
        if (refused) return;
        if (unconfirmed)
            dispatch(setSnackAction(`${plural(unconfirmed, "review")} not confirmed yet — refresh the run log to check.`, "warning"));
        else if (started)
            dispatch(setSnackAction(`Started ${plural(started, "review")} — follow ${started > 1 ? "them" : "it"} in the run log.`, "success"));
    };

    return (
        <Paper className={classes.root}>
            <Typography variant="h6" gutterBottom>
                Review documents
            </Typography>
            <Grid container spacing={3} alignItems="flex-start" style={{ marginBottom: 24 }}>
                <Grid size={{ xs: 12, md: 5 }}>
                    <TextField
                        select
                        label="Playbook"
                        value={selectedKey}
                        onChange={(e) => setPlaybookKey(e.target.value)}
                        disabled={busy || options.length === 0}
                        error={playbooks.isError || (!playbooks.isLoading && options.length === 0)}
                        helperText={
                            playbooks.isError
                                ? "Could not load the playbooks."
                                : !playbooks.isLoading && options.length === 0
                                  ? "No reviewable playbooks — enable one and approve some provisions first."
                                  : undefined
                        }
                        fullWidth
                    >
                        {options.map((p) => (
                            <MenuItem key={p.key} value={p.key}>
                                {p.displayName || p.key} (v{p.version}, {plural(p.provisionCount, "provision")})
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                        label="Client name"
                        placeholder="Acme Logistics"
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                        disabled={busy}
                        fullWidth
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }} style={{ paddingTop: 8 }}>
                    <Tooltip title="A test run is reviewed, stored and downloadable exactly like any other — the label only keeps playbook tuning out of the client work in the log.">
                        <FormControlLabel
                            control={<Checkbox checked={isTest} onChange={(e) => setIsTest(e.target.checked)} disabled={busy} />}
                            label="Mark these as test runs"
                        />
                    </Tooltip>
                </Grid>
            </Grid>

            <div {...getRootProps()} className={`${classes.zone} ${isDragActive ? classes.zoneActive : ""}`}>
                <input {...getInputProps()} />
                <UploadIcon />
                <Typography variant="body2">Drop Word documents here, or click to browse (.docx)</Typography>
            </div>

            {queue.map((entry) => (
                <Paper key={`${entry.file.name}-${entry.file.size}`} variant="outlined" className={classes.row}>
                    {stateIcon[entry.state]}
                    <Typography variant="body2" className={classes.name}>
                        {entry.file.name}
                    </Typography>
                    {entry.note && (
                        <Typography variant="caption" className={classes.note}>
                            {entry.note}
                        </Typography>
                    )}
                    {entry.state !== "starting" && !busy && (
                        <IconButton
                            size="small"
                            aria-label={`Remove ${entry.file.name}`}
                            onClick={() => setQueue((previous) => previous.filter((q) => q.file !== entry.file))}
                        >
                            <RemoveIcon fontSize="small" />
                        </IconButton>
                    )}
                </Paper>
            ))}

            <Box className={classes.actions}>
                <Button
                    variant="contained"
                    startIcon={<RunningIcon />}
                    onClick={process}
                    disabled={busy || !selectedKey || pendingCount === 0}
                >
                    {pendingCount > 1 ? `Review ${pendingCount} documents` : "Review document"}
                </Button>
                {queue.length > 0 && (
                    <Button
                        onClick={() => setQueue([])}
                        disabled={busy}
                    >
                        Clear queue
                    </Button>
                )}
            </Box>

        </Paper>
    );
};

export default RunUpload;
